<script setup lang="ts">
	import { ref, computed, onBeforeUnmount, watch } from "vue";
	import { useToast } from "vue-toastification";
	import { DOWNLOAD_FETCH_OPTIONS } from "@common-ui/media/downloadFile";
	import { downloadFile as downloadFileHelper, triggerBlobDownload } from "@/helpers/download";
	import DownloadInfoDialog from "./DownloadInfoDialog.vue";
	import { isPdfMimeType, isTextMimeType, isWordMimeType } from "./knowledgeMime";
	import { unwrapDefaultDocumentText } from "./sparkGuideDocumentText";
	import { getRowsFromTablePayload, normalizeTableRowsForColumns } from "./sparkGuideTableRows";
	import { parseCsvRecords } from "./csv";
	import type { KnowledgeRequirement, KnowledgeAssignment, TableColumnSchema, PackKnowledgeFulfillment, SparkGuideDraft } from "./types";
	import { couldNot } from "@/helpers/userMessages";
	import { useAuthSessionStore } from "@/stores/authSession";
	import {
		getRequirementDefaultDownloadUrl,
		getDocumentDownloadUrl,
		getTableDataUploadUrl,
		createDocument,
		getDocumentUploadUrl,
		confirmDocumentUpload,
		uploadToPresignedUrl,
		getTableData
	} from "./api";
	import {
		clearScribePendingDocumentUpload,
		createScribePendingUploadOwner,
		isScribeDocumentUploadConfirmed,
		isScribeDocumentUploadAttemptConfirmed,
		markScribePendingDocumentUploadConfirmed,
		readScribePendingDocumentUpload,
		recordScribeDocumentUploadConfirmed,
		saveScribePendingDocumentUpload,
		tryClearScribePendingDocumentUploadAttempt,
		tryReadScribePendingDocumentUpload,
		tryReadStableScribePendingDocumentUpload,
		type ScribePendingUploadOwner
	} from "@/db/scribePendingUploadDb";

	const props = withDefaults(
		defineProps<{
			requirement: KnowledgeRequirement;
			assignment?: KnowledgeAssignment | null;
			draft?: SparkGuideDraft | null;
			showActions?: boolean;
			showInlineRevert?: boolean;
			inlineRevertMode?: "local" | "request";
			inlineRevertDisabled?: boolean;
			actionsDisabled?: boolean;
			forceDefaultData?: boolean;
		}>(),
		{
			showActions: true,
			showInlineRevert: true,
			inlineRevertMode: "local",
			inlineRevertDisabled: false,
			actionsDisabled: false,
			forceDefaultData: false
		}
	);
	const emit = defineEmits<{ busy: [value: boolean]; uploading: [value: boolean]; dirty: [value: boolean]; revert: [] }>();

	const toast = useToast();
	const authSession = useAuthSessionStore();
	const currentPendingUploadOwner = (): ScribePendingUploadOwner | null =>
		createScribePendingUploadOwner(authSession.organizationId, authSession.userId);

	// State
	const loadingData = ref(false);
	const downloadInfoVisible = ref(false);
	const uploadConfirmVisible = ref(false);
	const fileInput = ref<HTMLInputElement | null>(null);
	const tablePage = ref(1);
	const tableRowsPerPage = ref(50);
	const tableRowsPerPageOptions = [25, 50, 100, 250];

	// Table data
	const tableRows = ref<Record<string, string>[]>([]);
	const originalTableRows = ref<Record<string, string>[]>([]);

	// Document/text data
	const textContent = ref("");
	const originalTextContent = ref("");
	const uploadedFile = ref<File | null>(null);
	const uploadedFileName = ref("");
	const uploadedFileMimeType = ref("");
	const savedDocumentId = ref<string | null>(null);
	const savedDocumentSavedAt = ref<number | null>(null);
	/**
	 * F-16 (b): a Document stub that has been created but whose bytes have not been confirmed yet.
	 *
	 * The scribe API issues a presigned upload URL only FOR an existing document
	 * (`getDocumentUploadUrl({ documentId })`), so the stub must exist before the PUT — the create
	 * cannot simply be moved after it. Retaining the id here means a retry after a failed upload REUSES
	 * that stub instead of minting a new one, which is what turns "one orphan per failed save" into
	 * "at most one orphan per editor session". Cleared on confirm (it is a real document now) and on
	 * every reset/reload (it belongs to a requirement this editor is no longer showing).
	 */
	const pendingDocumentId = ref<string | null>(null);
	const durablePendingAttemptId = ref<string | null>(null);
	const savedTableDataS3Key = ref<string | null>(null);
	const uploading = ref(false);
	let editRevision = 0;
	watch([tableRows, textContent, uploadedFile, uploadedFileName, uploadedFileMimeType], () => editRevision++, {
		deep: true,
		flush: "sync"
	});
	const getEditRevision = () => editRevision;
	const isBusy = computed(() => loadingData.value || uploading.value);

	watch(isBusy, (value) => emit("busy", value), { immediate: true });
	watch(uploading, (value) => emit("uploading", value), { immediate: true });

	// Computed helpers
	const isTable = computed(() => props.requirement.knowledgeType === "table");
	const isDocument = computed(() => props.requirement.knowledgeType === "document");
	const hasCustomizedAssignment = computed(() =>
		Boolean(!props.forceDefaultData && props.assignment && !props.assignment.useDefaultData)
	);

	const mimeType = computed(() => {
		if (uploadedFileMimeType.value) return uploadedFileMimeType.value;
		if (hasCustomizedAssignment.value && props.assignment?.knowledgeDocument) {
			return props.assignment.knowledgeDocument.mimeType || "";
		}
		return props.requirement.defaultDataMimeType || "";
	});

	const isTextDoc = computed(() => {
		if (!isDocument.value) return false;
		const mime = mimeType.value;
		if (!mime) return true;
		return isTextMimeType(mime);
	});

	const isPdfDoc = computed(() => isDocument.value && isPdfMimeType(mimeType.value));

	const isDocxDoc = computed(() => isDocument.value && isWordMimeType(mimeType.value));

	const currentFilename = computed(() => {
		if (uploadedFileName.value) return uploadedFileName.value;
		if (hasCustomizedAssignment.value && props.assignment?.knowledgeDocument?.filename) {
			return props.assignment.knowledgeDocument.filename;
		}
		return props.requirement.defaultDataFilename || "";
	});

	const columns = computed<TableColumnSchema[]>(() => {
		return props.requirement.tableSchema?.columns || [];
	});

	type LoadedGuideData = { kind: "table"; rows: Record<string, string>[] } | { kind: "text"; textContent: string } | { kind: "binary" };

	let loadRequestId = 0;
	let fileSelectionRequestId = 0;
	let abandonGenerationId = 0;
	let componentActive = true;
	const supersedePendingLoad = () => {
		loadRequestId += 1;
		loadingData.value = false;
	};
	const supersedePendingFileSelection = () => {
		fileSelectionRequestId += 1;
		uploading.value = false;
	};
	watch(
		() => props.actionsDisabled,
		(disabled) => {
			if (disabled && uploading.value) supersedePendingFileSelection();
		}
	);

	onBeforeUnmount(() => {
		componentActive = false;
		loadRequestId += 1;
		supersedePendingFileSelection();
	});

	const tablePageCount = computed(() => {
		const totalRows = tableRows.value.length;
		if (totalRows === 0) return 1;
		return Math.max(1, Math.ceil(totalRows / tableRowsPerPage.value));
	});

	const tableStartIndex = computed(() => (tablePage.value - 1) * tableRowsPerPage.value);

	const visibleTableRows = computed(() => {
		const start = tableStartIndex.value;
		return tableRows.value.slice(start, start + tableRowsPerPage.value);
	});

	// Cell-by-cell comparison with early exit — rows are flat string records, and this
	// computed re-runs on every keystroke, so serializing both tables would be wasteful.
	const tableHasChanges = computed(() => {
		if (!isTable.value) return false;
		const rows = tableRows.value;
		const originals = originalTableRows.value;
		if (rows.length !== originals.length) return true;
		const cols = columns.value;
		return rows.some((row, index) => cols.some((col) => row[col.name] !== originals[index][col.name]));
	});

	// Dirty signal: tables compare against original; docs are dirty when a binary file is staged OR typed text differs from the originally loaded text.
	const hasChanges = computed(() =>
		isTable.value ? tableHasChanges.value : Boolean(uploadedFile.value) || textContent.value !== originalTextContent.value
	);
	watch(hasChanges, (value) => emit("dirty", value));

	const isUsingDefault = computed(() => {
		return props.forceDefaultData || !props.assignment || props.assignment.useDefaultData;
	});

	const fileAcceptTypes = computed(() => {
		if (isTable.value) return ".csv";

		if (props.requirement.documentHints?.fileTypes?.length) {
			return props.requirement.documentHints.fileTypes.map((t) => (t.includes("/") ? t : t.startsWith(".") ? t : `.${t}`)).join(",");
		}

		const constraintMime = props.requirement.defaultDataMimeType;
		if (constraintMime) {
			if (constraintMime === "application/pdf") return ".pdf";
			if (
				constraintMime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
				constraintMime === "application/msword"
			)
				return ".doc,.docx";
			if (constraintMime === "text/csv") return ".csv";
			if (constraintMime === "text/markdown" || constraintMime === "application/markdown" || constraintMime.endsWith("/markdown"))
				return ".md,.markdown,.txt";
			if (constraintMime.startsWith("text/")) return ".txt,.md";
		}

		if (isPdfDoc.value) return ".pdf";
		if (isDocxDoc.value) return ".doc,.docx";
		if (isTextDoc.value) return ".txt,.md";
		return "*";
	});

	const loadData = async () => {
		const requestId = ++loadRequestId;
		const requirementId = props.requirement.id;
		const ownerOrganizationId = authSession.organizationId;
		const ownerUserId = authSession.userId;
		const pendingUploadOwner = createScribePendingUploadOwner(ownerOrganizationId, ownerUserId);
		const isCurrentRequest = () =>
			componentActive &&
			requestId === loadRequestId &&
			props.requirement.id === requirementId &&
			authSession.organizationId === ownerOrganizationId &&
			authSession.userId === ownerUserId;

		loadingData.value = true;
		uploadedFile.value = null;
		uploadedFileName.value = "";
		uploadedFileMimeType.value = "";
		savedDocumentId.value = null;
		savedDocumentSavedAt.value = null;
		// F-16 (b): a retained stub belongs to the requirement this editor was showing. Reusing it for a
		// different requirement's document would be worse than leaving it orphaned.
		pendingDocumentId.value = null;
		durablePendingAttemptId.value = null;
		savedTableDataS3Key.value = null;
		tablePage.value = 1;
		try {
			const loaded = isTable.value ? await loadTableData(isCurrentRequest) : await loadDocumentData(isCurrentRequest);
			if (!isCurrentRequest()) return;
			applyLoadedData(loaded);
			applyDraft();
			await applyDurablePendingUpload(isCurrentRequest, pendingUploadOwner);
		} catch (error) {
			if (!isCurrentRequest()) return;
			// The per-source loaders catch their own failures; this guards unexpected throws
			// (e.g. draft/normalization bugs) that would otherwise surface only as an
			// unhandled rejection from the `void loadData()` watcher.
			onLoadFailure("unexpected load failure", { requirementId: props.requirement.id, error });
		} finally {
			if (isCurrentRequest()) {
				loadingData.value = false;
			}
		}
	};

	// Parent-owned restores call this exposed path after changing the source of truth.
	// Unlike a passive assignment refresh, an explicit reload must discard any file
	// selection that began before the restore completed.
	const reloadData = async () => {
		supersedePendingFileSelection();
		await loadData();
	};

	const applyLoadedData = (loaded: LoadedGuideData) => {
		if (loaded.kind === "table") {
			tableRows.value = cloneRows(loaded.rows);
			originalTableRows.value = cloneRows(loaded.rows);
			return;
		}

		if (loaded.kind === "text") {
			textContent.value = loaded.textContent;
			originalTextContent.value = loaded.textContent;
			return;
		}

		if (loaded.kind === "binary") {
			textContent.value = "";
			originalTextContent.value = "";
		}
	};

	const applyDraft = () => {
		const draft = props.draft;
		if (!draft) return;

		if (draft.kind === "table" && isTable.value) {
			tableRows.value = cloneRows(draft.tableRows);
			originalTableRows.value = cloneRows(draft.originalTableRows);
			savedTableDataS3Key.value = draft.savedTableDataS3Key ?? null;
			return;
		}

		if (draft.kind === "text" && isDocument.value) {
			textContent.value = draft.textContent;
			originalTextContent.value = draft.originalTextContent;
			uploadedFileName.value = draft.uploadedFileName;
			uploadedFileMimeType.value = draft.uploadedFileMimeType || "text/plain";
			savedDocumentId.value = draft.savedDocumentId ?? null;
			savedDocumentSavedAt.value = draft.savedDocumentSavedAt ?? null;
			uploadedFile.value = null;
			return;
		}

		if (draft.kind === "binary" && isDocument.value) {
			textContent.value = "";
			originalTextContent.value = "";
			uploadedFileName.value = draft.uploadedFileName;
			uploadedFileMimeType.value = draft.uploadedFileMimeType || "application/octet-stream";
			savedDocumentId.value = draft.savedDocumentId ?? null;
			savedDocumentSavedAt.value = draft.savedDocumentSavedAt ?? null;
			uploadedFile.value = null;
		}
	};

	/**
	 * LSP-19313: resume a document Save whose in-process retries (F-16) exhausted, or that was
	 * mid-retry when the tab reloaded. `props.draft`'s binary/text kinds carry only the file NAME
	 * (see applyDraft above) — never bytes — so this durable IndexedDB row is the only place the
	 * actual bytes can survive a reload. Runs after applyDraft so an in-session wizard draft (which
	 * can reflect newer edits, e.g. a since-completed save) always wins over a possibly-stale durable
	 * row for the same requirement.
	 */
	const applyDurablePendingUpload = async (
		isCurrentRequest: () => boolean,
		pendingUploadOwner: ScribePendingUploadOwner | null
	): Promise<void> => {
		if (!isDocument.value || !pendingUploadOwner) return;
		const requirementId = props.requirement.id;
		let pending = await readScribePendingDocumentUpload(requirementId, pendingUploadOwner);
		if (!isCurrentRequest()) return;
		if (!pending) return;
		const getConfirmation = async (upload: NonNullable<typeof pending>) => {
			const attemptConfirmation = await isScribeDocumentUploadAttemptConfirmed(upload.attemptId);
			if (attemptConfirmation === "confirmed" || !upload.pendingDocumentId) return attemptConfirmation;
			const documentConfirmation = await isScribeDocumentUploadConfirmed(upload.pendingDocumentId, upload.sequence);
			if (documentConfirmation === "confirmed") return "confirmed";
			if (attemptConfirmation === "unknown" || documentConfirmation === "unknown") return "unknown";
			return "not-confirmed";
		};
		const confirmation = await getConfirmation(pending);
		if (!isCurrentRequest()) return;
		if (pending.status === "confirmed" || confirmation === "confirmed") {
			console.warn("SparkGuideEditor: confirmed durable upload cleanup was incomplete; discarding terminal row", {
				requirementId,
				attemptId: pending.attemptId,
				documentId: pending.pendingDocumentId
			});
			await clearScribePendingDocumentUpload(requirementId, pendingUploadOwner, pending.attemptId);
			return;
		}
		if (confirmation === "unknown") return;
		const draftSaveIsStale =
			Boolean(savedDocumentId.value) && (savedDocumentSavedAt.value == null || pending.createdAt <= savedDocumentSavedAt.value);
		if (uploadedFile.value || draftSaveIsStale) {
			// A fresher in-session draft or an already-confirmed save is authoritative; a durable row
			// left behind for this requirement is now stale. Log it — otherwise a stale row that never
			// gets applied looks identical to "no pending upload" from the outside.
			console.warn("SparkGuideEditor: durable pending upload found but superseded by fresher local state; discarding", {
				requirementId,
				hasUploadedFile: Boolean(uploadedFile.value),
				draftSaveIsStale,
				savedDocumentSavedAt: savedDocumentSavedAt.value,
				pendingCreatedAt: pending.createdAt
			});
			await clearScribePendingDocumentUpload(requirementId, pendingUploadOwner, pending.attemptId);
			return;
		}
		// The first await may have returned a snapshot that another tab has since replaced or
		// confirmed. Re-read durable state immediately before applying bytes; unlike the former
		// module-local revision counter, this check observes every browser context.
		const currentPending = await readScribePendingDocumentUpload(requirementId, pendingUploadOwner);
		if (!isCurrentRequest()) return;
		if (currentPending?.attemptId !== pending.attemptId || currentPending.status !== "pending") return;
		const currentConfirmation = await getConfirmation(currentPending);
		if (!isCurrentRequest() || currentConfirmation !== "not-confirmed") return;
		const freshestPending = await readScribePendingDocumentUpload(requirementId, pendingUploadOwner);
		if (!isCurrentRequest()) return;
		if (freshestPending?.attemptId !== currentPending.attemptId || freshestPending.status !== "pending") return;
		const freshestConfirmation = await getConfirmation(freshestPending);
		if (!isCurrentRequest() || freshestConfirmation !== "not-confirmed") return;
		pending = freshestPending;
		if (pending.kind === "binary") {
			textContent.value = "";
			uploadedFile.value = new File([pending.payloadBytes], pending.fileName, { type: pending.mimeType });
		} else {
			textContent.value = new TextDecoder().decode(pending.payloadBytes);
			uploadedFile.value = null;
		}
		uploadedFileName.value = pending.fileName;
		uploadedFileMimeType.value = pending.mimeType;
		pendingDocumentId.value = pending.pendingDocumentId;
		durablePendingAttemptId.value = pending.attemptId;
		console.warn("SparkGuideEditor: recovered a durably-persisted document Save that did not finish uploading", {
			requirementId,
			fileName: pending.fileName,
			hadPendingDocumentId: Boolean(pending.pendingDocumentId)
		});
		toast.warning(`We recovered "${pending.fileName}" from an interrupted save. Click Save to finish uploading it.`);
	};

	// Silent-load failures previously left an empty editor the user could save over real
	// data — every load fail-condition must log, and active failures also surface a toast.
	const onLoadFailure = (context: string, detail: Record<string, unknown>, showToast = true) => {
		console.warn("SparkGuideEditor: " + context, detail);
		if (showToast) toast.error("We couldn't load this Spark Guide's content. Please try again.");
	};

	const createEmptyRow = (): Record<string, string> => {
		const row: Record<string, string> = {};
		columns.value.forEach((col) => {
			row[col.name] = "";
		});
		return row;
	};

	// Rows are always flat Record<string, string> (see normalizeTableRow), so a shallow
	// per-row copy is a full clone.
	const cloneRows = (rows: Record<string, string>[]): Record<string, string>[] => rows.map((row) => ({ ...row }));

	const loadTableData = async (isCurrentRequest: () => boolean): Promise<LoadedGuideData> => {
		const requirementId = props.requirement.id;
		const failTable = (context: string, detail: Record<string, unknown>) => {
			onLoadFailure(context, { requirementId, ...detail }, isCurrentRequest());
			return { kind: "table" as const, rows: [createEmptyRow()] };
		};

		const cols = columns.value;
		if (cols.length === 0) {
			return { kind: "table", rows: [] };
		}

		if (isUsingDefault.value) {
			if (Array.isArray(props.requirement.defaultData)) {
				return { kind: "table", rows: normalizeTableRowsForColumns(props.requirement.defaultData, columns.value) };
			} else if (props.requirement.defaultDataS3Key) {
				try {
					const { downloadUrl } = await getRequirementDefaultDownloadUrl(props.requirement.id);
					const response = await fetch(downloadUrl);
					if (response.ok) {
						const text = await response.text();
						const parsed = JSON.parse(text) as unknown;
						return { kind: "table", rows: normalizeTableRowsForColumns(getRowsFromTablePayload(parsed), columns.value) };
					} else {
						return failTable("default table data fetch returned non-ok", { status: response.status });
					}
				} catch (error) {
					return failTable("default table data load threw", { error });
				}
			} else {
				return { kind: "table", rows: [createEmptyRow()] };
			}
		} else if (props.assignment?.knowledgeTableId) {
			try {
				const result = await getTableData(props.assignment.knowledgeTableId);
				if (Array.isArray(result.data)) {
					return {
						kind: "table",
						rows: normalizeTableRowsForColumns(result.data, columns.value, props.assignment.columnMapping)
					};
				} else {
					return failTable("assignment table data was not an array", { knowledgeTableId: props.assignment.knowledgeTableId });
				}
			} catch (error) {
				return failTable("assignment table data load threw", { knowledgeTableId: props.assignment.knowledgeTableId, error });
			}
		} else {
			return { kind: "table", rows: [createEmptyRow()] };
		}
	};

	const loadDocumentData = async (isCurrentRequest: () => boolean): Promise<LoadedGuideData> => {
		if (!isTextDoc.value) return { kind: "binary" };

		const requirementId = props.requirement.id;
		const failText = (context: string, detail: Record<string, unknown>) => {
			onLoadFailure(context, { requirementId, ...detail }, isCurrentRequest());
			return { kind: "text" as const, textContent: "" };
		};

		if (isUsingDefault.value) {
			if (typeof props.requirement.defaultData === "string") {
				return { kind: "text", textContent: props.requirement.defaultData };
			} else if (props.requirement.defaultDataS3Key) {
				try {
					const { downloadUrl } = await getRequirementDefaultDownloadUrl(props.requirement.id);
					const response = await fetch(downloadUrl);
					if (response.ok) {
						return {
							kind: "text",
							textContent: unwrapDefaultDocumentText(await response.text(), () =>
								// Schema-invalid envelope-like JSON stays editable; diagnose it without a load-failure toast.
								onLoadFailure("default document envelope was invalid", { requirementId }, false)
							)
						};
					} else {
						return failText("default document fetch returned non-ok", { status: response.status });
					}
				} catch (error) {
					return failText("default document load threw", { error });
				}
			} else {
				return { kind: "text", textContent: "" };
			}
		} else if (props.assignment?.knowledgeDocumentId) {
			try {
				const { downloadUrl } = await getDocumentDownloadUrl(props.assignment.knowledgeDocumentId);
				const response = await fetch(downloadUrl);
				if (response.ok) {
					return { kind: "text", textContent: await response.text() };
				} else {
					return failText("assignment document fetch returned non-ok", {
						knowledgeDocumentId: props.assignment.knowledgeDocumentId,
						status: response.status
					});
				}
			} catch (error) {
				return failText("assignment document load threw", { knowledgeDocumentId: props.assignment.knowledgeDocumentId, error });
			}
		} else {
			return { kind: "text", textContent: "" };
		}
	};

	// Load immediately and whenever the load-source props change. KnowledgeEditDialog
	// swaps them on a live instance; the wizard instead remounts per step via :key.
	// A same-guide assignment refresh can arrive after Revert to Original and after the
	// user has already selected their next file. Keep that staged edit instead of letting
	// the late refresh clear it. Enabling force-default still reloads the true original;
	// disabling it keeps the replacement already held by the editor.
	let watchedRequirementId: string | null = null;
	let watchedForceDefaultData = props.forceDefaultData;
	let watchedOrganizationId = authSession.organizationId;
	let watchedUserId = authSession.userId;
	watch(
		() => [props.requirement, props.forceDefaultData, props.assignment, authSession.organizationId, authSession.userId],
		() => {
			const requirementChanged = watchedRequirementId !== props.requirement.id;
			const forceDefaultChanged = watchedForceDefaultData !== props.forceDefaultData;
			const ownerChanged = watchedOrganizationId !== authSession.organizationId || watchedUserId !== authSession.userId;
			const restoringOriginal = forceDefaultChanged && props.forceDefaultData;
			const keepingStagedReplacement = forceDefaultChanged && !props.forceDefaultData;
			watchedRequirementId = props.requirement.id;
			watchedForceDefaultData = props.forceDefaultData;
			watchedOrganizationId = authSession.organizationId;
			watchedUserId = authSession.userId;
			if (requirementChanged || ownerChanged) {
				supersedePendingFileSelection();
				void loadData();
				return;
			}
			if (keepingStagedReplacement) {
				console.warn("SparkGuideEditor: preserved the staged replacement after forced-original mode ended", {
					requirementId: props.requirement.id,
					forceDefaultData: props.forceDefaultData,
					hasChanges: hasChanges.value,
					savedDocumentId: savedDocumentId.value,
					savedTableDataS3Key: savedTableDataS3Key.value
				});
				return;
			}
			if (restoringOriginal) supersedePendingFileSelection();
			const hasLocallySavedDocument = isDocument.value && Boolean(savedDocumentId.value);
			const hasLocallySavedTable = isTable.value && Boolean(savedTableDataS3Key.value);
			if (!restoringOriginal && (hasChanges.value || hasLocallySavedDocument || hasLocallySavedTable)) {
				console.warn("SparkGuideEditor: preserved local changes during a passive source refresh", {
					requirementId: props.requirement.id,
					assignmentId: props.assignment?.id ?? null,
					forceDefaultData: props.forceDefaultData,
					hasChanges: hasChanges.value,
					savedDocumentId: savedDocumentId.value,
					savedTableDataS3Key: savedTableDataS3Key.value
				});
				return;
			}
			void loadData();
		},
		{ immediate: true }
	);

	const addRow = () => {
		if (props.actionsDisabled) return;
		tableRows.value.push(createEmptyRow());
		tablePage.value = tablePageCount.value;
	};

	const removeRow = (visibleIndex: number) => {
		if (props.actionsDisabled) return;
		if (tableRows.value.length > 1) {
			tableRows.value.splice(toAbsoluteRowIndex(visibleIndex), 1);
		}
	};

	const resetToOriginal = () => {
		supersedePendingFileSelection();
		if (isTable.value) {
			tableRows.value = cloneRows(originalTableRows.value);
			tablePage.value = 1;
			return;
		}

		textContent.value = originalTextContent.value;
		uploadedFile.value = null;
		uploadedFileName.value = "";
		uploadedFileMimeType.value = "";
		savedDocumentId.value = null;
		savedDocumentSavedAt.value = null;
		// F-16 (b): the user abandoned this edit, so the retained stub is not going to be filled by a
		// retry of it. Drop the id rather than attaching the next, unrelated document to it.
		pendingDocumentId.value = null;
	};

	const abandonPendingState = async (options: { silent?: boolean } = {}): Promise<boolean> => {
		abandonGenerationId += 1;
		// Invalidate synchronously: a recovery read that already captured a row must not apply it
		// while durable deletion is in progress.
		supersedePendingLoad();
		supersedePendingFileSelection();
		if (!isDocument.value) {
			resetToOriginal();
			return true;
		}
		const requirementId = props.requirement.id;
		const pendingUploadOwner = currentPendingUploadOwner();
		if (!pendingUploadOwner) {
			resetToOriginal();
			return true;
		}
		try {
			let targetAttemptId: string | undefined = durablePendingAttemptId.value ?? undefined;
			let targetIdentityEstablished = Boolean(targetAttemptId);
			if (!targetIdentityEstablished) {
				const targetReadResult = await tryReadStableScribePendingDocumentUpload(requirementId, pendingUploadOwner);
				if (targetReadResult.succeeded) {
					targetAttemptId = targetReadResult.upload?.attemptId;
					targetIdentityEstablished = true;
				}
			}
			if (!targetIdentityEstablished) {
				console.warn("SparkGuideEditor: could not identify the pending upload to abandon", { requirementId });
				if (!options.silent)
					toast.error("We couldn't discard this Spark Guide safely. Please keep this window open and try again.");
				await loadData();
				return false;
			}
			// Drop the visible edit immediately so parents cannot remount or inspect a half-abandoned
			// editor while IndexedDB work is pending. A persistent failure reloads the still-durable row.
			resetToOriginal();
			if (!targetAttemptId) return true;
			await clearScribePendingDocumentUpload(requirementId, pendingUploadOwner, targetAttemptId);
			const firstReadResult = await tryReadScribePendingDocumentUpload(requirementId, pendingUploadOwner);
			const firstAttemptSucceeded = firstReadResult.succeeded && !firstReadResult.upload;
			if (firstAttemptSucceeded) {
				durablePendingAttemptId.value = null;
				return true;
			}
			if (firstReadResult.succeeded && firstReadResult.upload?.attemptId !== targetAttemptId) {
				console.warn("SparkGuideEditor: a newer pending upload replaced the attempt being abandoned", {
					requirementId,
					targetAttemptId,
					currentAttemptId: firstReadResult.upload?.attemptId ?? null
				});
			} else {
				for (let clearAttempt = 0; clearAttempt < 2; clearAttempt += 1) {
					const clearResult = await tryClearScribePendingDocumentUploadAttempt(
						requirementId,
						pendingUploadOwner,
						targetAttemptId
					);
					if (clearResult === "different-attempt") break;
					if (clearResult === "cleared") {
						durablePendingAttemptId.value = null;
						return true;
					}
				}
			}
			console.warn("SparkGuideEditor: could not abandon pending upload without resurrection risk", { requirementId });
			if (!options.silent) toast.error("We couldn't discard this Spark Guide safely. Please keep this window open and try again.");
			await loadData();
			return false;
		} catch (error) {
			console.warn("SparkGuideEditor.abandonPendingState: failed to abandon pending upload safely", { requirementId, error });
			if (!options.silent) toast.error("We couldn't discard this Spark Guide safely. Please keep this window open and try again.");
			try {
				await loadData();
			} catch (loadError) {
				console.warn("SparkGuideEditor.abandonPendingState: failed to reload data after abandon failure", {
					requirementId,
					error: loadError
				});
			}
			return false;
		}
	};

	const revertToOriginal = abandonPendingState;

	const revertButtonDisabled = computed(
		() => props.actionsDisabled || (props.inlineRevertMode === "local" ? !hasChanges.value : props.inlineRevertDisabled)
	);

	const onInlineRevert = async () => {
		if (props.actionsDisabled) return;
		if (props.inlineRevertMode === "request") {
			// The parent reloads immediately after this event. Clear first so that reload cannot
			// recover the same bytes the user just chose to abandon.
			if (!(await abandonPendingState())) return;
			emit("revert");
			return;
		}

		if (!(await revertToOriginal())) return;
		emit("revert");
	};

	const toAbsoluteRowIndex = (visibleIndex: number): number => {
		return tableStartIndex.value + visibleIndex;
	};

	watch([tableRowsPerPage, () => tableRows.value.length], () => {
		if (tablePage.value > tablePageCount.value) {
			tablePage.value = tablePageCount.value;
		}
	});

	// Download
	const onDownloadClick = () => {
		if (isTable.value) {
			downloadInfoVisible.value = true;
		} else {
			downloadFile();
		}
	};

	const downloadTableAsCsv = () => {
		const cols = columns.value;
		const header = cols.map((column) => escapeCsvField(column.name)).join(",");
		const rows = tableRows.value.map((row) => cols.map((column) => escapeCsvField(row[column.name] || "")).join(","));
		const csv = [header, ...rows].join("\n");
		const blob = new Blob([csv], { type: "text/csv" });
		triggerBlobDownload(blob, (props.requirement.name || "table") + ".csv");
	};

	const downloadFile = async () => {
		const requirementId = props.requirement.id;
		const fallbackDefaultFilename = props.requirement.defaultDataFilename || "";
		const fallbackDefaultMimeType = props.requirement.defaultDataMimeType || "";
		const savedCustomizedDocumentId = savedDocumentId.value;
		const assignmentDocumentId = hasCustomizedAssignment.value ? props.assignment?.knowledgeDocumentId : null;
		const customizedDocumentId = savedCustomizedDocumentId ?? assignmentDocumentId;
		const fallbackCustomizedFilename = savedCustomizedDocumentId
			? uploadedFileName.value
			: props.assignment?.knowledgeDocument?.filename || "";
		let filename = customizedDocumentId ? fallbackCustomizedFilename : fallbackDefaultFilename;

		try {
			// Download reflects persisted server state; unsaved editor state never changes its source or metadata.
			const result = customizedDocumentId
				? await getDocumentDownloadUrl(customizedDocumentId)
				: await getRequirementDefaultDownloadUrl(requirementId);
			if (!result.downloadUrl) {
				console.warn("SparkGuideEditor: download url missing", {
					requirementId,
					customizedDocumentId
				});
				toast.error(couldNot("download the file"));
				return;
			}
			filename = result.filename || filename || "download";
			const defaultMimeType = result.mimeType || fallbackDefaultMimeType;
			if (!customizedDocumentId && !defaultMimeType) {
				// The editor's empty-MIME authoring fallback cannot classify persisted bytes, which may be binary.
				console.warn("SparkGuideEditor: default download MIME missing", { requirementId });
				toast.error(couldNot("download the file"));
				return;
			}
			if (!customizedDocumentId && isTextMimeType(defaultMimeType)) {
				const response = await fetch(result.downloadUrl, DOWNLOAD_FETCH_OPTIONS);
				if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
				triggerBlobDownload(
					new Blob(
						[
							unwrapDefaultDocumentText(await response.text(), () =>
								console.warn("SparkGuideEditor: default document envelope was invalid", { requirementId })
							)
						],
						{ type: defaultMimeType }
					),
					filename
				);
				return;
			}
			if ((await downloadFileHelper(result.downloadUrl, filename, { failOnRejectedSignedUrl: true })) === false) {
				console.warn("SparkGuideEditor: document download could not start", {
					requirementId,
					customizedDocumentId,
					filename
				});
				toast.error(couldNot("download the file"));
			}
		} catch (error) {
			console.error("SparkGuideEditor: download failed", { error, requirementId, customizedDocumentId, filename });
			toast.error(couldNot("download the file"));
		}
	};

	const escapeCsvField = (value: string): string => {
		if (value.includes(",") || value.includes('"') || value.includes("\n")) {
			return '"' + value.replace(/"/g, '""') + '"';
		}
		return value;
	};

	// Upload

	const onUploadClick = () => {
		if (props.actionsDisabled) return;
		if (tableHasChanges.value) {
			uploadConfirmVisible.value = true;
			return;
		}
		fileInput.value?.click();
	};

	const confirmUpload = () => {
		if (props.actionsDisabled) return;
		uploadConfirmVisible.value = false;
		fileInput.value?.click();
	};

	const onFileSelected = async (event: Event) => {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) return;
		if (props.actionsDisabled) {
			input.value = "";
			return;
		}
		const selectedRequirementId = props.requirement.id;
		const selectionRequestId = ++fileSelectionRequestId;
		const isCurrentSelection = () =>
			componentActive && props.requirement.id === selectedRequirementId && selectionRequestId === fileSelectionRequestId;
		const warnDiscardedSelection = (stage: string) => {
			console.warn("SparkGuideEditor: discarded a stale file selection", {
				stage,
				componentActive,
				selectedRequirementId,
				currentRequirementId: props.requirement.id,
				selectionRequestId,
				currentSelectionRequestId: fileSelectionRequestId
			});
		};

		if (isTable.value) {
			const ext = file.name.split(".").pop()?.toLowerCase();
			if (ext !== "csv") {
				uploading.value = false;
				toast.error("Please upload a CSV file.");
				input.value = "";
				return;
			}
		}

		uploading.value = true;
		try {
			if (isTable.value) {
				if (await parseTableFile(file, isCurrentSelection, warnDiscardedSelection)) {
					supersedePendingLoad();
				}
			} else {
				const fileMime = file.type;
				const fileExt = file.name.split(".").pop()?.toLowerCase() ?? "";
				const fileHasTextExtension = ["txt", "md", "markdown", "text"].includes(fileExt);
				const fileIsText =
					isTextMimeType(fileMime) || (fileHasTextExtension && (!fileMime || fileMime === "application/octet-stream"));

				if (fileIsText) {
					const text = await file.text();
					if (!isCurrentSelection()) {
						warnDiscardedSelection("text file read");
						return;
					}
					const inferredTextMime = ["md", "markdown"].includes(fileExt) ? "text/markdown" : "text/plain";
					textContent.value = text;
					uploadedFileName.value = file.name;
					uploadedFileMimeType.value = isTextMimeType(fileMime) ? fileMime : inferredTextMime;
					uploadedFile.value = null;
				} else {
					if (!isCurrentSelection()) {
						warnDiscardedSelection("binary file selection");
						return;
					}
					uploadedFile.value = file;
					uploadedFileName.value = file.name;
					uploadedFileMimeType.value = file.type || "application/octet-stream";
				}
				supersedePendingLoad();
			}
		} catch (error) {
			if (!isCurrentSelection()) {
				warnDiscardedSelection("file read failure");
				return;
			}
			console.warn("SparkGuideEditor: failed to read the selected file", { fileName: file.name, error });
			toast.error(couldNot("read that file"));
		} finally {
			if (isCurrentSelection()) uploading.value = false;
			// Reset inside finally so a failed read doesn't leave the input holding the file,
			// which would suppress the change event when the user re-selects the same file.
			input.value = "";
		}
	};

	const parseTableFile = async (
		file: File,
		isCurrentSelection: () => boolean,
		warnDiscardedSelection: (stage: string) => void
	): Promise<boolean> => {
		const text = await file.text();
		if (!isCurrentSelection()) {
			warnDiscardedSelection("table file read");
			return false;
		}
		const records = parseCsvRecords(text).filter((record) => record.some((field) => field.trim()));
		if (records.length === 0) {
			console.warn("SparkGuideEditor: uploaded CSV is empty", { fileName: file.name, requirementId: props.requirement.id });
			toast.error("That file looks empty. Please upload a CSV with a header row and data.");
			return false;
		}

		const cols = columns.value;
		const headers = records[0];

		const headerMap = new Map<string, number>();
		headers.forEach((header, index) => {
			headerMap.set(header.trim().toLowerCase(), index);
		});

		// A CSV whose headers match none of the schema columns would silently replace the
		// user's table with blank cells — reject it and name the expected columns instead.
		const matchedColumns = cols.filter((col) => headerMap.has(col.name.toLowerCase()));
		if (matchedColumns.length === 0) {
			console.warn("SparkGuideEditor: uploaded CSV headers match no schema columns", {
				fileName: file.name,
				requirementId: props.requirement.id,
				expected: cols.map((col) => col.name),
				received: headers
			});
			toast.error(`That file's columns don't match this guide. Expected columns: ${cols.map((col) => col.name).join(", ")}.`);
			return false;
		}

		const rows: Record<string, string>[] = [];
		for (let i = 1; i < records.length; i++) {
			const values = records[i];
			const row: Record<string, string> = {};
			cols.forEach((col) => {
				const idx = headerMap.get(col.name.toLowerCase());
				row[col.name] = idx !== undefined && idx < values.length ? values[idx] : "";
			});
			rows.push(row);
		}

		tableRows.value = rows.length > 0 ? rows : [createEmptyRow()];
		uploadedFileName.value = file.name;
		return true;
	};

	async function buildFulfillment(): Promise<PackKnowledgeFulfillment | null> {
		if (!hasChanges.value) return null;
		if (isTable.value) {
			const data = JSON.stringify(tableRows.value);
			const blob = new Blob([data], { type: "application/json" });
			const filename = (props.requirement.name || "table") + ".json";
			const uploadResult = await getTableDataUploadUrl({ filename, fileSize: blob.size, requirementId: props.requirement.id });
			await uploadToPresignedUrl(uploadResult.uploadUrl, blob, "application/json");
			const tableColumnMapping: Record<string, string> = {};
			columns.value.forEach((col) => {
				tableColumnMapping[col.name] = col.name;
			});
			return {
				requirementId: props.requirement.id,
				knowledgeType: "table",
				useDefaultData: false,
				tableDataS3Key: uploadResult.s3Key,
				tableColumnMapping
			};
		}
		// Document: binary staged file OR typed/loaded text
		const startingAbandonGenerationId = abandonGenerationId;
		const isBinary = uploadedFile.value !== null;
		const file = uploadedFile.value;
		const blob = isBinary ? (file as File) : new Blob([textContent.value], { type: "text/plain" });
		const mime = isBinary ? file!.type || "application/octet-stream" : "text/plain";
		const filenameBase = (props.requirement?.name || "document").replace(/[\\/]/g, "-").slice(0, 120);
		const filename = isBinary ? file!.name : `${filenameBase}.txt`;
		const pendingUploadOwner = currentPendingUploadOwner();
		if (!pendingUploadOwner) {
			throw new Error("Cannot save a Spark Guide document without an authenticated organization and user");
		}
		// F-16 (b) — CONTRACT, verified 2026-08-26: the stub CANNOT be created after the PUT.
		// `getDocumentUploadUrl` takes a `documentId` (`components/scribe/api.ts`), so the presigned URL is
		// issued FOR an existing Document; there is nothing to PUT to until the stub exists. Reordering is
		// therefore impossible without a scribe-API change (an upload-url endpoint that mints the document,
		// or a document-delete endpoint — neither exists in admin's scribe API surface today).
		//
		// What is fixable is the ORPHAN RATE. Previously every failed Save minted a brand-new stub, so a
		// user retrying a flaky upload three times left three orphaned Documents behind. The stub id is now
		// retained across failures within this editor's lifetime and REUSED: the presigned URL is re-issued
		// against the same document, so a retry costs no new stub. One editor session with N failed saves
		// leaves at most one orphan instead of N, and a save that eventually succeeds leaves none.
		const doc = pendingDocumentId.value
			? { id: pendingDocumentId.value }
			: (
					await createDocument({
						title: props.requirement?.name || "Document",
						description: props.requirement?.description ?? null
					})
				).document;
		if (!pendingDocumentId.value) pendingDocumentId.value = doc.id;
		const uploadResult = await getDocumentUploadUrl({ documentId: doc.id, filename, mimeType: mime, fileSize: blob.size });
		// LSP-19313: durably persist the bytes and the stub id BEFORE the PUT, so if every in-process
		// retry (F-16) exhausts, or the tab reloads mid-retry, applyDurablePendingUpload can resume this
		// exact upload on the next load instead of silently losing it.
		const persistenceResult = await saveScribePendingDocumentUpload({
			...pendingUploadOwner,
			requirementId: props.requirement.id,
			pendingDocumentId: doc.id,
			kind: isBinary ? "binary" : "text",
			fileName: filename,
			mimeType: mime,
			payload: blob,
			createdAt: Date.now()
		});
		if (persistenceResult.persisted) durablePendingAttemptId.value = persistenceResult.attemptId;
		const { attemptId } = persistenceResult;
		try {
			await uploadToPresignedUrl(uploadResult.uploadUrl, blob, mime);
			await confirmDocumentUpload(doc.id);
			const confirmationRecorded = await recordScribeDocumentUploadConfirmed(attemptId, doc.id);
			if (!confirmationRecorded) {
				console.warn("SparkGuideEditor: server confirmation could not be fenced from local recovery", {
					requirementId: props.requirement.id,
					documentId: doc.id,
					attemptId
				});
				toast.error("Your Spark Guide was saved, but local recovery could not be secured. Please contact support.");
			}
			// Confirmed: this stub is no longer an orphan candidate, and the NEXT save (a different file) must
			// mint its own document rather than overwriting this one. The durable copy is equally stale now.
			if (pendingDocumentId.value === doc.id) pendingDocumentId.value = null;
			// A failed put leaves the row it meant to supersede untouched. Carry that exact identity into
			// cleanup so the confirmed save cannot leave stale bytes recoverable, while the CAS clear still
			// protects a newer row written by another tab.
			const cleanupAttemptId = persistenceResult.persisted ? attemptId : persistenceResult.supersededAttemptId;
			let durableUploadResolved = !persistenceResult.persisted && cleanupAttemptId === null;
			for (let cleanupAttempt = 0; cleanupAttempt < 2 && !durableUploadResolved && cleanupAttemptId; cleanupAttempt += 1) {
				await clearScribePendingDocumentUpload(props.requirement.id, pendingUploadOwner, cleanupAttemptId);
				const verification = await tryReadScribePendingDocumentUpload(props.requirement.id, pendingUploadOwner);
				if (verification.succeeded) {
					durableUploadResolved = !verification.upload || verification.upload.attemptId !== cleanupAttemptId;
				}
			}
			let markedConfirmed = false;
			if (!durableUploadResolved) {
				markedConfirmed = cleanupAttemptId
					? await markScribePendingDocumentUploadConfirmed(props.requirement.id, pendingUploadOwner, cleanupAttemptId)
					: false;
				if (!markedConfirmed) {
					console.warn("SparkGuideEditor: confirmed upload could not be made terminal in durable storage", {
						requirementId: props.requirement.id,
						documentId: doc.id
					});
					toast.error("Your Spark Guide was saved, but local cleanup failed. Please contact support before editing it again.");
				}
			}
			if ((durableUploadResolved || markedConfirmed) && cleanupAttemptId && durablePendingAttemptId.value === cleanupAttemptId) {
				durablePendingAttemptId.value = null;
			}
		} finally {
			if (persistenceResult.persisted && abandonGenerationId !== startingAbandonGenerationId) {
				const clearResult = await tryClearScribePendingDocumentUploadAttempt(props.requirement.id, pendingUploadOwner, attemptId);
				console.warn("SparkGuideEditor: discarded a durable upload persisted after the edit was abandoned", {
					requirementId: props.requirement.id,
					attemptId,
					clearResult
				});
				if (clearResult === "cleared" && durablePendingAttemptId.value === attemptId) {
					durablePendingAttemptId.value = null;
				}
			}
		}
		return {
			requirementId: props.requirement.id,
			knowledgeType: "document",
			useDefaultData: false,
			existingKnowledgeDocumentId: doc.id
		};
	}

	function markSaved(fulfillment?: PackKnowledgeFulfillment | null) {
		if (isTable.value) {
			originalTableRows.value = cloneRows(tableRows.value);
			if (fulfillment?.knowledgeType === "table" && !fulfillment.useDefaultData) {
				savedTableDataS3Key.value = fulfillment.tableDataS3Key;
			}
			return;
		}

		if (fulfillment?.knowledgeType === "document" && !fulfillment.useDefaultData) {
			savedDocumentId.value = fulfillment.existingKnowledgeDocumentId;
			savedDocumentSavedAt.value = Date.now();
		}

		if (uploadedFile.value) {
			uploadedFileName.value = uploadedFile.value.name;
			uploadedFileMimeType.value = uploadedFile.value.type || "application/octet-stream";
			uploadedFile.value = null;
			return;
		}

		originalTextContent.value = textContent.value;
	}

	function getDraft(): SparkGuideDraft | null {
		if (isTable.value) {
			return {
				kind: "table",
				tableRows: cloneRows(tableRows.value),
				originalTableRows: cloneRows(originalTableRows.value),
				...(savedTableDataS3Key.value ? { savedTableDataS3Key: savedTableDataS3Key.value } : {})
			};
		}

		if (isTextDoc.value || isTextMimeType(uploadedFileMimeType.value)) {
			return {
				kind: "text",
				textContent: textContent.value,
				originalTextContent: originalTextContent.value,
				uploadedFileName: uploadedFileName.value,
				uploadedFileMimeType: uploadedFileMimeType.value,
				...(savedDocumentId.value
					? { savedDocumentId: savedDocumentId.value, savedDocumentSavedAt: savedDocumentSavedAt.value }
					: {})
			};
		}

		return {
			kind: "binary",
			uploadedFileName: uploadedFileName.value || currentFilename.value,
			uploadedFileMimeType: uploadedFileMimeType.value || mimeType.value,
			...(savedDocumentId.value ? { savedDocumentId: savedDocumentId.value, savedDocumentSavedAt: savedDocumentSavedAt.value } : {})
		};
	}

	defineExpose({
		abandonPendingState,
		buildFulfillment,
		download: onDownloadClick,
		getDraft,
		getEditRevision,
		hasChanges,
		isBusy,
		markSaved,
		reloadData,
		tableRows,
		revertToOriginal,
		upload: onUploadClick
	});
</script>

<template>
	<div class="spark-guide-editor d-flex flex-column">
		<!-- Loading indicator -->
		<div v-if="loadingData || uploading" class="d-flex flex-column align-center justify-center py-8">
			<v-progress-circular indeterminate color="primary" size="36" class="mb-3" />
			<span class="text-body-2 text-medium-emphasis">{{ uploading ? "Loading file..." : "Loading data..." }}</span>
		</div>

		<!-- Table content -->
		<div v-else-if="isTable" class="table-content">
			<div class="table-scroll">
				<v-table density="compact" class="knowledge-table">
					<thead>
						<tr>
							<th v-for="col in columns" :key="col.name" class="text-left text-caption">
								{{ col.name }}
							</th>
							<th class="text-center" style="width: 48px"></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(row, rowIdx) in visibleTableRows" :key="toAbsoluteRowIndex(rowIdx)">
							<td v-for="col in columns" :key="col.name" class="pa-1">
								<v-text-field
									v-model="row[col.name]"
									density="compact"
									hide-details
									single-line
									:disabled="actionsDisabled"
									class="table-cell-input" />
							</td>
							<td class="text-center pa-1">
								<v-btn
									icon
									variant="text"
									size="x-small"
									:disabled="actionsDisabled || tableRows.length <= 1"
									@click="removeRow(rowIdx)">
									<v-icon size="16" color="error">mdi-close</v-icon>
								</v-btn>
							</td>
						</tr>
					</tbody>
				</v-table>
			</div>
			<div class="table-controls">
				<div class="d-flex align-center justify-space-between flex-wrap ga-3">
					<v-btn variant="text" color="primary" size="small" prepend-icon="mdi-plus" :disabled="actionsDisabled" @click="addRow">
						ADD ROW
					</v-btn>
					<div class="d-flex align-center ga-3 flex-wrap justify-end">
						<div class="text-caption text-medium-emphasis">
							Showing
							{{ tableRows.length === 0 ? 0 : tableStartIndex + 1 }}
							-
							{{ Math.min(tableStartIndex + tableRowsPerPage, tableRows.length) }}
							of {{ tableRows.length }} rows
						</div>
						<v-select
							v-model="tableRowsPerPage"
							:items="tableRowsPerPageOptions"
							label="Rows per page"
							density="compact"
							hide-details
							style="max-width: 160px" />
						<v-pagination v-model="tablePage" :length="tablePageCount" density="comfortable" total-visible="7" />
					</div>
				</div>
				<div v-if="showActions" class="d-flex ga-2 flex-wrap mt-2">
					<v-btn variant="outlined" rounded="xl" @click="onDownloadClick"> Download File </v-btn>
					<v-btn variant="outlined" rounded="xl" :disabled="actionsDisabled" @click="onUploadClick"> Upload File </v-btn>
					<v-btn
						v-if="showInlineRevert"
						color="warning"
						variant="outlined"
						rounded="xl"
						:disabled="revertButtonDisabled"
						@click="onInlineRevert">
						Revert to Original
					</v-btn>
					<slot name="actions-end" :has-changes="hasChanges" :is-binary-doc="false" />
				</div>
			</div>
		</div>

		<!-- Text document content (editable) -->
		<div v-else-if="isTextDoc" class="document-content">
			<v-textarea
				v-model="textContent"
				class="text-document-editor"
				label="CONTENT"
				density="comfortable"
				hide-details="auto"
				:disabled="actionsDisabled"
				counter />
		</div>

		<!-- PDF document display -->
		<div v-else-if="isPdfDoc" class="d-flex align-center ga-3 pa-4 binary-doc-display">
			<v-icon size="48" color="red">mdi-file-pdf-box</v-icon>
			<div>
				<div class="text-body-2 font-weight-medium">{{ currentFilename || "PDF Document" }}</div>
				<div class="text-caption text-medium-emphasis">PDF file - upload a new file to replace</div>
			</div>
		</div>

		<!-- DOCX document display -->
		<div v-else-if="isDocxDoc" class="d-flex align-center ga-3 pa-4 binary-doc-display">
			<v-icon size="48" color="blue">mdi-file-word-box</v-icon>
			<div>
				<div class="text-body-2 font-weight-medium">{{ currentFilename || "Word Document" }}</div>
				<div class="text-caption text-medium-emphasis">Word file - upload a new file to replace</div>
			</div>
		</div>

		<!-- Download action -->
		<div v-if="showActions && !isTable" class="document-actions d-flex align-center ga-2 flex-wrap mt-4">
			<v-btn variant="outlined" rounded="xl" @click="onDownloadClick"> Download File </v-btn>
			<v-btn variant="outlined" rounded="xl" :disabled="actionsDisabled" @click="onUploadClick"> Upload File </v-btn>
			<v-btn
				v-if="showInlineRevert"
				color="warning"
				variant="outlined"
				rounded="xl"
				:disabled="revertButtonDisabled"
				@click="onInlineRevert">
				Revert to Original
			</v-btn>
			<slot name="actions-end" :has-changes="hasChanges" :is-binary-doc="isPdfDoc || isDocxDoc" />
		</div>

		<!-- Hidden file input -->
		<input ref="fileInput" type="file" :accept="fileAcceptTypes" style="display: none" @change="onFileSelected" />

		<!-- Download info dialog (for tables) -->
		<DownloadInfoDialog v-model="downloadInfoVisible" @confirm="downloadTableAsCsv" />

		<!-- Upload confirmation dialog (when table has unsaved changes) -->
		<v-dialog v-model="uploadConfirmVisible" max-width="400">
			<v-card class="pa-2" rounded="xl" elevation="8">
				<v-card-title>Replace table data?</v-card-title>
				<v-card-text>
					You've made changes to the table. Uploading a file will replace all your current rows with the contents of the file.
				</v-card-text>
				<v-card-actions class="d-flex justify-end ga-2">
					<v-btn variant="text" rounded="xl" @click="uploadConfirmVisible = false">Cancel</v-btn>
					<v-btn color="primary" variant="flat" rounded="xl" :disabled="actionsDisabled" @click="confirmUpload">
						Upload File
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<style scoped>
	.knowledge-table {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-width: 0;
		border: 1px solid rgba(0, 0, 0, 0.12);
		border-radius: 6px;
		overflow: hidden;
	}

	.knowledge-table :deep(table) {
		width: 100%;
		table-layout: fixed;
	}

	.knowledge-table th {
		position: sticky;
		top: 0;
		z-index: 2;
		height: 42px;
		font-size: 0.75rem !important;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: rgba(var(--v-theme-on-surface), 0.7);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		background: rgb(var(--v-theme-surface));
		border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
		box-shadow: 0 1px 0 rgba(var(--v-theme-on-surface), 0.08);
	}

	.knowledge-table td {
		height: 42px;
		min-width: 0;
		overflow: hidden;
		border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
	}

	.table-cell-input {
		width: 100%;
		min-width: 0;
	}

	.table-cell-input :deep(.v-input__control),
	.table-cell-input :deep(.v-field),
	.table-cell-input :deep(.v-field__field) {
		min-width: 0;
	}

	.table-cell-input :deep(.v-field) {
		font-size: 0.875rem;
		border-radius: 6px;
		background-color: rgba(var(--v-theme-surface), 0.88);
	}

	.table-cell-input :deep(.v-field__input) {
		padding: 4px 8px;
		min-width: 0;
		min-height: 32px;
	}

	.table-content {
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
		min-height: 0;
	}

	.table-scroll {
		flex: 1 1 auto;
		width: 100%;
		min-height: 0;
		min-width: 0;
		overflow: hidden;
	}

	.knowledge-table :deep(.v-table__wrapper) {
		flex: 1 1 auto;
		width: 100%;
		min-height: 0;
		min-width: 0;
		overflow-x: auto;
		overflow-y: auto;
	}

	.table-controls {
		flex: 0 0 auto;
		padding-top: 12px;
		padding-bottom: 8px;
		border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
		background: rgb(var(--v-theme-surface));
	}

	.document-content {
		display: flex;
		flex: 1 1 auto;
		min-height: 0;
	}

	.text-document-editor {
		flex: 1 1 auto;
		min-height: 0;
	}

	.text-document-editor :deep(.v-input__control),
	.text-document-editor :deep(.v-field),
	.text-document-editor :deep(.v-field__field),
	.text-document-editor :deep(.v-field__input) {
		height: 100%;
		min-height: 0;
	}

	.text-document-editor :deep(textarea) {
		height: 100%;
		overflow-y: auto;
		resize: none;
	}

	.document-actions {
		flex: 0 0 auto;
	}

	.binary-doc-display {
		border: 1px solid rgba(0, 0, 0, 0.12);
		border-radius: 8px;
		background-color: rgba(0, 0, 0, 0.02);
	}

	.spark-guide-editor {
		flex: 1 1 auto;
		min-height: 0;
	}
</style>
